import * as React from "react";
import "./FilterCtl.scss";
import { ListItem } from "@testout/testout-commerce/models/ListItem";

interface IFilterProps {
    values?: string[];
    disabled?: boolean;
    className?: string;
    name: string;
    label?: string;
    onChange?: (name: string, values: string[]) => void;
    onSelect: (name: string, searchText?: string) => Promise<ListItem[]>;
    style?: React.CSSProperties;
    hideItemValue?: boolean;
    minCharsForSearch?: number;
    multiSelect?: boolean;
}

interface IFilterState {
    isOpen: boolean;
    inProgress: boolean;
    items: ListItem[];
    focusIndex: number;
    searchValue: string;
    error: string;
}

export default class FilterCtl extends React.PureComponent<IFilterProps, IFilterState> {
    constructor(props: IFilterProps) {
        super(props);
        this.state = {
            isOpen: false,
            inProgress: false,
            //selectedItem: { value: this.props.value || "", name: "" },
            items: [],
            focusIndex: -1,
            searchValue: "",
            error: "",
        };
    }

    _controlRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLDivElement>();
    _scrollerRef = React.createRef<HTMLDivElement>();
    _itemRef = React.createRef<HTMLLIElement>();
    _searchRef = React.createRef<HTMLInputElement>();
    _needFocus = false;

    onFilterClick = () => {
        if (this.props.disabled) return;
        this._needFocus = true;
        if (this.state.isOpen)
            this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
        else {
            this.fetchItems(this.state.searchValue);
            this.setState({ isOpen: true });
        }
    }

    onFilterKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.disabled) return;
        if (e.key === "Enter") {
            this._needFocus = true;
            if (this.state.isOpen) {
                if (this.state.focusIndex >= 0)
                    this.onItemClick(this.state.focusIndex);
                else
                    this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
            } else {
                this.fetchItems(this.state.searchValue);
                this.setState({ isOpen: true });
            }
            e.preventDefault();
        } else if (e.key === "Escape") {
            if (this.state.isOpen) {
                this._needFocus = true;
                this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
            }
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            console.log(e.key);
            if (this.state.focusIndex < this.state.items.length - 1) {
                this.setState({ focusIndex: this.state.focusIndex + 1 });
                if (!this.state.isOpen)
                    this.onItemClick(this.state.focusIndex + 1);
            }
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            if (this.state.focusIndex > 0) {
                this.setState({ focusIndex: this.state.focusIndex - 1 });
                if (!this.state.isOpen)
                    this.onItemClick(this.state.focusIndex - 1);
            }
            e.preventDefault();
        }
    }

    onSearchValueChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        this.updateList(e.target.value);
    }

    private _debounceTimer: number | undefined = undefined;
    async updateList(value: string) {
        this.setState({ searchValue: value });
        if (this._debounceTimer)
            window.clearTimeout(this._debounceTimer);
        this._debounceTimer = window.setTimeout(async () => {
            this._debounceTimer = undefined;
            await this.fetchItems(value);
        }, 300);
    }

    async fetchItems(value: string) {
        this.setState({ inProgress: true, focusIndex: -1 });
        try {
            const items = await this.props.onSelect(this.props.name, value.length >= (this.props.minCharsForSearch ?? 3) ? value: undefined);
            this.setState({ inProgress: false, items: items });
        } catch (error: any) {
            this.setState({ inProgress: false, items: [], error: `Search failed. ${error.message}` });
        }
    }

    onItemClick = (itemIndex: number) => {
        this._needFocus = true;
        const item = this.state.items[itemIndex];
        let values = [...(this.props.values ?? [])];
        if (this.props.multiSelect) {
            // If an item contains multiple sub-values (like the "My Territory"" item for a multi-territory salesperson)
            let subValues = item.value?.split(',');
            if (subValues) {
                // Determine if all of the sub-values have been selected
                var containsAll = subValues?.every(v => values.includes(v)) ?? false;
                for (const subValue of subValues) {
                    const i = values.indexOf(subValue);
                    // If the selected value is not there but it should be because it is a sub-value, then add it
                    if (!containsAll && i < 0)
                        values.push(subValue);
                    // Or if the selected value is there but should not be, remove it
                    else if (containsAll && i >= 0)
                        values.splice(i, 1);
                }
            }
            else {
                const i = values.indexOf(item.name);
                if (i >= 0)
                    values.splice(i, 1);
                else
                    values.push(item.name);
            }
        }
        else
            values = [item.value ?? item.name];

        this.setState({ isOpen: !!this.props.multiSelect, inProgress: false, focusIndex: !!this.props.multiSelect ? this.state.focusIndex : -1, searchValue: !!this.props.multiSelect ? this.state.searchValue : "", error: "" });
        if (this.props.onChange) this.props.onChange(this.props.name, values);
    }

    onItemClearClick = () => {
        this._needFocus = true;
        this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
        if (this.props.onChange) this.props.onChange(this.props.name, []);
    }

    onDocumentFocusIn = (e: FocusEvent) => {
        const filter = this._controlRef.current;
        if (filter && !filter.contains(e.target as Node))
            this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
    }

    onDocumentClick = (e: FocusEvent) => {
        const filter = this._controlRef.current;
        if (filter && !filter.contains(e.target as Node))
            this.setState({ isOpen: false, inProgress: false, focusIndex: -1, searchValue: "", error: "" });
    }

    ensureItemVisible() {
        if (this.state.isOpen && this.state.focusIndex >= 0 && this._scrollerRef.current && this._itemRef.current) {
            const liItem = this._itemRef.current as HTMLLIElement;
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            if (liItem.offsetTop < divScroller.offsetTop + divScroller.scrollTop)
                divScroller.scrollTop = liItem.offsetTop - divScroller.offsetTop;
            if (liItem.offsetTop + liItem.offsetHeight > divScroller.offsetTop + divScroller.scrollTop + divScroller.clientHeight)
                divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - (divScroller.offsetTop + divScroller.clientHeight);
        }
    }

    setFocus() {
        this._needFocus = false;
        const inpSearch = this._searchRef.current as HTMLInputElement;
        const divValue = this._valueRef.current as HTMLDivElement;
        if (this.state.isOpen)
            inpSearch.focus();
        else
            divValue.focus();
    }

    componentDidMount() {
        document.addEventListener("click", this.onDocumentClick);
        document.addEventListener("focusin", this.onDocumentFocusIn);
        this.ensureItemVisible();
    }

    componentDidUpdate() {
        this.ensureItemVisible();
        if (this._needFocus)
            this.setFocus();
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
        document.removeEventListener("click", this.onDocumentClick);
    }

    render() {
        const { items, focusIndex, isOpen, inProgress, searchValue, error } = this.state;
        const { values, multiSelect, hideItemValue, style, disabled, name, label, minCharsForSearch } = this.props;
        return <div className={"filter" + (this.props.className ? " " + this.props.className : "")}
            style={style} ref={this._controlRef}>
            <div className={(values?.length ? "active" : "") + (disabled ? " disabled" : "")}>
                <div className="value" title={values?.join(", ")}
                    tabIndex={disabled ? undefined : 0} onClick={this.onFilterClick} onKeyDown={this.onFilterKeyDown} ref={this._valueRef}>
                    {!!values?.length && `(${values.length}) `}
                    {label ?? name}                    
                </div>
                {!!values?.length &&
                    <button title="Clear" className="clear" onClick={() => this.onItemClearClick() }>&#xe5cd;</button>}
            </div>
            {isOpen &&
                <div className="drop-down">
                    <div className="search">
                        <div>
                            <input type="text" ref={this._searchRef}
                                placeholder="Search"
                                value={searchValue}
                                onChange={this.onSearchValueChange}
                                onKeyDown={this.onFilterKeyDown} />
                            <button title="Search"></button>
                        </div>
                    </div>
                    <div className="scroller" ref={this._scrollerRef}>
                        <ul className={multiSelect ? "multi-select" : undefined}>
                            {!searchValue
                                ? <li key="_msg">Type to search</li>
                                : searchValue.length < (minCharsForSearch ?? 3)
                                    ? <li key="_msg">Type at least {minCharsForSearch ?? 3} characters to start search</li>
                                    : inProgress
                                        ? <li key="_msg">Searching...</li>
                                        : error
                                            ? <li key="_msg" className="error">{error}</li>
                                            : items.length === 0
                                                ? <li key="_msg">No results</li>
                                                : <li key="_msg" className="results">Results</li>
                            }
                            {this.state.items.map((item: ListItem, i) => {
                                var selected = false;
                                if (values) {
                                    // Get any sub-values for an item (like the "My Territory"" item for a multi-territory salesperson)
                                    var subValues = item.value?.split(',');
                                    // If all of the sub-values for an item have been selected, then select the item
                                    var contains = subValues?.every(v => values.includes(v)) ?? false;
                                    // Or if the name has been selected, then select that item
                                    selected = (contains || values.includes(item.name)) ?? false;
                                }
                                return <li key={i} ref={i === focusIndex ? this._itemRef : undefined}
                                    className={(i === focusIndex ? "focus" : "") + (selected ? " selected" : "")}
                                    onClick={() => this.onItemClick(i)}>
                                    {item.name}
                                    {item.value && item.value !== item.name && !hideItemValue &&
                                        <span>{item.value}</span>}
                                    {item.description &&
                                        <div>{item.description}</div>}
                                </li>
                            })}
                        </ul>
                    </div>
                </div>}
        </div>;
    }
}